import {Component, OnInit, ViewChild, OnDestroy}    from '@angular/core';
import {FormBuilder, FormGroup, Validators}         from "@angular/forms";
import {NgbActiveModal}                             from "@ng-bootstrap/ng-bootstrap";

import {AngularQuery}                               from "../../services/query/angularquery";
import {Message}                                    from "../../services/message/message";
import {MessageEntry}                               from "../../services/message/message-entry";
import {Source}                                     from "../../services/source/source";
import {SourceService}                              from "../../services/source/source.service";

import {Subscription}                               from "rxjs/Subscription";



@Component({
	selector: 'app-source-admin',
	templateUrl: './source-admin.component.html',
	styleUrls: ['./source-admin.component.css']
})



/**
 * Create, edit or delete a Source.
 */
export class SourceAdminComponent implements OnInit, OnDestroy {


	inEditMode:boolean = false;
	message:Message;
	source: Source;
	sourceSubscription:Subscription;
	tForm: FormGroup;



	/**
	 * Initialize the object.
	 *
	 * @param {NgbActiveModal} activeModal     Used to close the page.
	 * @param {FormBuilder} fb                 Used to create the page objects.
	 * @param {SourceService}  sourceService   Used when modifying Source objects.
	 */
	constructor(
		public activeModal: NgbActiveModal,
		private fb: FormBuilder,
		private sourceService: SourceService,
	) {}


	/**
	 * Initialize the FormBuilder and send the 'Ready' message.
	 */
	ngOnInit() {

		// Initialize the data structure for the page.
		this.tForm = this.fb.group({
			name: ['', [Validators.required,Validators.minLength(1), Validators.maxLength(255),]],
			id: [-1, [Validators.required, Validators.min(-1)]],
		});

		// Set up the message window with an initial message.
		let me:MessageEntry = new MessageEntry();
		me.message = 'Ready';
		me.level = 1;
		this.message = new Message(me);
		this.message.show();

	}


	/**
	 * Unsubscribe to avoid memory leaks.
	 */
	ngOnDestroy() {

		if (this.sourceSubscription) {
			this.sourceSubscription.unsubscribe();
		}

	}


	/**
	 * Lets me talk to the Source typeahead.
	 */
	@ViewChild('sourceTypeahead')
	sourceTypeahead: any;


	/**
	 * Fill the fields with default data.
	 */
	clear():void {

		this.inEditMode = false;
		this.tForm.controls['name'].setValue('');
		this.tForm.controls['id'].setValue(-1);
		this.sourceTypeahead.searchFailed = false;
		this.sourceTypeahead.searching = false;
		this.sourceTypeahead.typeaheadSourceInput.nativeElement.value = '';

		// Notify the user.
		let me:MessageEntry = new MessageEntry();
		me.message = 'Field cleared';
		me.level = 1;
		this.message.add(me, true);
		this.message.show();

	}


	/**
	 * Delete the current item.
	 */
	deleteSource():void {

		// Give them the option to cancel.
		if (! window.confirm('Are you sure you want to delete this source?')) {
			return;
		}

		if (0 >= this.tForm.controls['id'].value) {

			this.clear();
			this.sourceTypeahead.typeaheadSourceInput.nativeElement.value = '';
			return;

		}

		let sourceName:string = this.sourceTypeahead.typeaheadSourceInput.nativeElement.value;

		if (this.sourceSubscription) {

			this.sourceSubscription.unsubscribe();

		}

		this.sourceSubscription = this.sourceService.deleteSource(this.tForm.controls['id'].value).subscribe(

			() => {

				this.clear();

				// Notify the user.
				let me:MessageEntry = new MessageEntry();
				me.message = "'" + sourceName + "' deleted";
				me.level = 2;
				this.message.add(me, true);
				this.message.show();

			},
			() => {

				// Notify the user.
				let me:MessageEntry = new MessageEntry();
				me.message = "Failed to delete '" + sourceName + "'";
				me.level = 4;
				this.message.add(me, true);
				this.message.show();

			}

		);

	}


	/**
	 * Returns true if the Delete and Edit buttons should be hidden.  This prohibits anyone from deleting or editing
	 * the 'No Source' record or deletion or editing of other records by non-administrators.
	 *
	 * @return {boolean} True if it should be hidden, false otherwise.
	 */
	hideDeleteEditButtons():boolean {

		return (1 >= this.tForm.controls['id'].value);

	}


	/**
	 * Update everything when the selection changes.
	 *
	 * @param {any} event  The new Source object.
	 */
	onSourceChange(event: any): void {

		let source: Source = new Source(event);
		this.tForm.controls['name'].setValue(source.name);
		this.tForm.controls['id'].setValue(source.id);

		// This uses a reference to the typeahead to set the text box to the selected tag.
		this.sourceTypeahead.typeaheadSourceInput.nativeElement.value = source.name;

		// Notify the user.
		let me:MessageEntry = new MessageEntry();
		me.message = "'" + source.name + "' chosen";
		me.level = 2;
		this.message.add(me, true);
		this.message.show();

	}


	/**
	 * Saved the edited Source value.
	 */
	save():void {

		let sourceName:string = this.tForm.controls['name'].value;
		let sourceId:number = this.tForm.controls['id'].value;

		if (this.sourceSubscription) {

			this.sourceSubscription.unsubscribe();

		}

		// Build the query string and the JSON data.
		let query: AngularQuery = new AngularQuery();
		query.setEndpoint(AngularQuery.ENDPOINT_SOURCE, sourceId.toString());
		let json:object = {};
		json['id']      = sourceId;
		json['name']    = sourceName;

		// Perform the update.
		this.sourceSubscription = this.sourceService.save( query.getUrl(), JSON.stringify( json ) ).subscribe(

			() => {

				this.sourceTypeahead.typeaheadSourceInput.nativeElement.value = sourceName;
				this.inEditMode = false;

				// Notify the user.
				let me:MessageEntry = new MessageEntry();
				me.message = "'" + sourceName + "' updated";
				me.level = 2;
				this.message.add(me, true);
				this.message.show();

			},
			() => {

				// Notify the user.
				let me:MessageEntry = new MessageEntry();
				me.message = "Failed to update '" + sourceName + "'";
				me.level = 4;
				this.message.add(me, true);
				this.message.show();

			}

		);

	}


	/**
	 * Set whether we are in edit or search mode.
	 *
	 * @param mode  True if in edit mode, false otherwise.
	 */
	setEditMode(mode:boolean): void {

		if (this.inEditMode && this.sourceTypeahead.typeaheadSourceInput.nativeElement.value !== this.tForm.controls['name'].value) {

			if (false === confirm("You edited the Source. Do you want to save changes? Click 'Cancel' and save your work or 'OK' to abandon the changes")) {

				return;

			}

			this.tForm.controls['name'].setValue(this.sourceTypeahead.typeaheadSourceInput.nativeElement.value);

		}

		this.inEditMode = mode;

	}

}



// WEBPACK FOOTER //
// ./src/app/admin/source/source-admin.component.ts